'use client';

import { useState, useMemo, useCallback, memo } from 'react';
import { Typography } from '../kit';
import UnifiedBookingDetailsSheet from './common/UnifiedBookingDetailsSheet';
import SortableDataTable from './common/SortableDataTable';
import { CompactParcelCard, SmartFilter, ModernPagination, LayoutContainer } from '../kit';
import { transformBooking } from '../transformations/parcelDataTransformations';
import { PARCEL_STATUS, PARCEL_FILTERS } from '../constants/parcelEnums';
import { BookingHistoryShimmer } from './common/LoadingShimmer';
import { filterAndSortBookings } from '../kit/bookingUtils';
import { MESSAGES } from '@/constants';
import {
  Box,
  Stack,
  Alert,
  Container,
  alpha,
  Card,
  CardContent,
  Chip,
  Fab,
  Zoom,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import {
  History,
  LocalShipping,
  TrendingUp,
  CheckCircle,
  Cancel,
  Add,
  ViewModule,
  ViewList,
  Visibility,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { themeColors } from '../theme/theme';
import { useRouter } from 'next/navigation';
import { useParcelsHistoryQuery } from '@/hooks/useParcelsQuery';
import { useTranslation } from '@/kit/useTranslation';

const ITEMS_PER_PAGE = 10;

function BookingHistory() {
  const router = useRouter();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState(PARCEL_STATUS.ALL);
  const [sortBy, setSortBy] = useState('time');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('table');
  const { t } = useTranslation();

  // Use React Query for history - shares cache with main parcels query
  const { data: parcelsData = [], isLoading: loading, error, refetch } = useParcelsHistoryQuery();

  const statusOptions = useMemo(
    () => [
      ...Object.values(PARCEL_STATUS),
      { value: PARCEL_FILTERS.MULTI_STAGE, label: t('booking.multiStage') },
    ],
    [t]
  );

  const sortOptions = useMemo(
    () => [
      { value: 'time', label: t('booking.recent') },
      { value: 'status', label: t('booking.status') },
      { value: 'price', label: t('booking.price') },
    ],
    [t]
  );

  const handleClose = useCallback(() => setSelected(null), []);
  const handleCardClick = useCallback((booking) => setSelected(booking), []);

  const bookings = useMemo(() => {
    // return parcelsData.map(transformBooking);
    return parcelsData;
  }, [parcelsData]);

  const sorted = useMemo(() => {
    let filtered = filterAndSortBookings(bookings, statusFilter, sortBy);
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (booking) =>
          booking.parcelId?.toString().toLowerCase().includes(query) ||
          booking.from?.label?.toLowerCase().includes(query) ||
          booking.to?.label?.toLowerCase().includes(query) ||
          booking.status?.toLowerCase().includes(query)
      );
    }
    return filtered;
  }, [bookings, statusFilter, sortBy, searchQuery]);

  // Smart Insights
  const insights = useMemo(() => {
    const total = bookings.length;
    const completed = bookings.filter((b) =>
      ['DELIVERED', 'COMPLETED'].includes(b.status?.toUpperCase())
    ).length;
    const cancelled = bookings.filter((b) => b.status?.toUpperCase() === 'CANCELLED').length;
    const active = bookings.filter((b) =>
      ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(b.status?.toUpperCase())
    ).length;
    const multiStage = bookings.filter((b) => b.isMultiStage || b.totalLegs > 1).length;
    const successRate = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, cancelled, active, multiStage, successRate };
  }, [bookings]);

  const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sorted.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sorted, currentPage]);

  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (loading) return <BookingHistoryShimmer />;

  return (
    // <LayoutContainer>
    <Box sx={{ background: '#fafafa', minHeight: '100vh', pb: 10 }}>
      {/* Page Title Section - Clean & Minimal */}
      <Box
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          py: 2,
          px: { xs: 2, md: 3 },
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 700,
                  color: '#1f2937',
                }}
              >
                {t('booking.bookingHistory')}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                {t('booking.bookingsFound', { count: sorted.length })}
              </Typography>
            </Box>
            {/* Quick Stats & View Toggle */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip
                label={t('booking.deliveredCount', { count: insights.completed })}
                size="small"
                sx={{
                  bgcolor: '#dcfce7',
                  color: '#15803d',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  display: { xs: 'none', sm: 'flex' },
                }}
              />
              {insights.active > 0 && (
                <Chip
                  label={t('booking.activeCount', { count: insights.active })}
                  size="small"
                  sx={{
                    bgcolor: '#dbeafe',
                    color: '#1d4ed8',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    display: { xs: 'none', sm: 'flex' },
                  }}
                />
              )}
              {/* View Toggle - Desktop only */}
              {isDesktop && (
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newView) => newView && setViewMode(newView)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid #e5e7eb',
                      px: 1.5,
                      py: 0.5,
                      '&.Mui-selected': {
                        bgcolor: themeColors.primaryColor,
                        color: '#fff',
                        '&:hover': {
                          bgcolor: alpha(themeColors.primaryColor, 0.85),
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="cards" aria-label="card view">
                    <ViewModule fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="table view">
                    <ViewList fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 2, px: { xs: 2, md: 3 } }}>
        {/* Smart Filter */}
        <SmartFilter
          options={statusOptions}
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setCurrentPage(1);
          }}
          onSearch={(query) => {
            setSearchQuery(query);
            setCurrentPage(1);
          }}
          searchPlaceholder={t('booking.searchPlaceholder')}
          sortOptions={sortOptions}
          onSortChange={setSortBy}
          currentSort={sortBy}
          quickFilterCount={4}
        />

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {MESSAGES.ERROR.LOAD_HISTORY}
          </Alert>
        )}

        {/* Empty State */}
        {paginatedBookings.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <LocalShipping sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#374151',
                mb: 1,
              }}
            >
              {MESSAGES.EMPTY.NO_BOOKINGS}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
              {t('booking.startCreating')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Table View - Desktop only */}
            {viewMode === 'table' && isDesktop ? (
              <SortableDataTable
                columns={[
                  {
                    field: 'parcelId',
                    label: 'ID',
                    type: 'id',
                    sortable: true,
                  },
                  {
                    field: 'parcelPickupAddress',
                    label: 'From',
                    type: 'address',
                    maxWidth: 180,
                    render: (val) => val?.label || '-',
                  },
                  {
                    field: 'parcelDeliveryAddress',
                    label: 'To',
                    type: 'address',
                    maxWidth: 180,
                    render: (val) => val?.label || '-',
                  },
                  {
                    field: 'parcelStatus',
                    label: 'Status',
                    type: 'status',
                    sortable: true,
                  },
                  {
                    field: 'parcelCreatedAt',
                    label: 'Date',
                    type: 'date',
                    sortable: true,
                  },
                  {
                    field: 'parcelPrice',
                    label: 'Price',
                    type: 'currency',
                    sortable: true,
                  },
                ]}
                data={paginatedBookings}
                onRowClick={handleCardClick}
                onDownload={(row) => {
                  // Generate invoice download
                  window.open(`/api/invoice/${row.id || row.parcelId}`, '_blank');
                }}
                initialSortField="date"
                initialSortDirection="desc"
                emptyMessage={MESSAGES.EMPTY.NO_BOOKINGS}
              />
            ) : (
              /* Cards View */
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                  },
                  gap: 2,
                }}
              >
                {paginatedBookings.map((booking) => (
                  <CompactParcelCard
                    key={booking.id || booking.parcelId}
                    booking={booking}
                    onClick={handleCardClick}
                  />
                ))}
              </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <ModernPagination
                  page={currentPage}
                  totalPages={totalPages}
                  onChange={handlePageChange}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* FAB */}
      <Zoom in={true}>
        <Fab
          color="primary"
          onClick={() => router.push('/booking')}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            bgcolor: themeColors.primaryColor,
            '&:hover': { bgcolor: '#4f46e5' },
          }}
        >
          <Add />
        </Fab>
      </Zoom>

      {/* Details Sheet */}
      {selected && <UnifiedBookingDetailsSheet booking={selected} onClose={handleClose} />}
    </Box>
    // </LayoutContainer>
  );
}

// Insight Chip
const InsightChip = ({ icon, label, highlight }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      bgcolor: highlight ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)',
      backdropFilter: 'blur(8px)',
      borderRadius: 2,
      px: 1.5,
      py: 0.75,
      flexShrink: 0,
    }}
  >
    {icon && React.cloneElement(icon, { sx: { fontSize: 16, opacity: 0.9 } })}
    <Typography
      sx={{
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: '#fff',
      }}
    >
      {label}
    </Typography>
  </Box>
);

import React from 'react';
export default memo(BookingHistory);
